<!--
  Grid cell field card: drag-to-move, edge resize, type/parent badges, remove on hover.
-->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { VIcons } from '@/shared/ui'
import { VTooltip } from 'vuetify/components'
import { useTranslate } from '@/shared/lib'
import type { DragPayload, GridFieldType, PassportFieldPaletteSource } from '../model/types'
import { createDragPayload } from '../model/useDragDrop'

const ROW_COLS = 12
const MIN_SPAN = 1

const props = withDefaults(
  defineProps<{
    field: GridFieldType
    /** Sidebar palette source — drives left stripe (matches `.pb-field-chip--*` tokens). */
    paletteSource?: PassportFieldPaletteSource
    fieldTypeBadge?: string
    parentBadge?: string
    /** Display label to show in info tooltip / settings modal: `<field_id> | <field_name>`. */
    fieldInfo?: string | null
  }>(),
  {
    paletteSource: 'own',
    fieldTypeBadge: '',
    parentBadge: '',
    fieldInfo: null,
  },
)

type GridFieldChromePayload = {
  id: string
  row: number
  cell: number
}

type FieldSettingsPayload = GridFieldChromePayload & {
  mode: 'catalog' | 'reference'
}

const emit = defineEmits<{
  remove: [payload: GridFieldChromePayload]
  resize: [payload: GridFieldChromePayload & { newSpan: number }]
  rename: [payload: GridFieldChromePayload & { name: string }]
  settings: [payload: FieldSettingsPayload]
  dragstart: [payload: DragPayload, event: DragEvent]
}>()

const rootRef = ref<HTMLElement | null>(null)
const isResizing = ref(false)
const previewSpan = ref<number | null>(null)
const editing = ref(false)
const renameDraft = ref('')
const renameInputRef = ref<HTMLInputElement | null>(null)
const { trs } = useTranslate()

watch(
  () => props.field.name,
  (n) => {
    if (!editing.value) renameDraft.value = n
  },
)

const gridColumnStyle = computed(() => {
  const span = previewSpan.value ?? props.field.length
  return { gridColumn: `span ${span}` as const }
})

const cardClass = computed(() => ({
  'pb-field-card--own': props.paletteSource === 'own',
  'pb-field-card--directory': props.paletteSource === 'parent',
  'pb-field-card--lists': props.paletteSource === 'list',
  'pb-field-card--table-view': props.field.view_type === 'table',
  'pb-field-card--resizing': isResizing.value,
}))

const displayName = computed(() =>
  props.field.view_type === 'table' ? `${trs('itf.table')}: ${props.field.name}` : props.field.name,
)

function itemTypeForDrag(): 'field' | 'heading' | 'table' {
  if (props.field.type === 'heading') return 'heading'
  if (props.field.type === 'table') return 'table'
  return 'field'
}

function startRename() {
  editing.value = true
  renameDraft.value = props.field.name
  nextTick(() => {
    renameInputRef.value?.focus()
    renameInputRef.value?.select()
  })
}

function cancelRename() {
  editing.value = false
  renameDraft.value = props.field.name
}

function commitRename() {
  if (!editing.value) return
  const n = renameDraft.value.trim()
  if (!n) {
    cancelRename()
    return
  }
  editing.value = false
  if (n !== props.field.name) {
    emit('rename', {
      id: props.field.id,
      row: props.field.row,
      cell: props.field.cell,
      name: n,
    })
  }
}

function openSettings() {
  emit('settings', {
    id: props.field.id,
    row: props.field.row,
    cell: props.field.cell,
    mode: props.paletteSource === 'element' ? 'reference' : 'catalog',
  })
}

function onDragStart(event: DragEvent) {
  if (isResizing.value || editing.value) {
    event.preventDefault()
    return
  }
  const f = props.field
  const payload = createDragPayload('grid', itemTypeForDrag(), {
    id: f.id,
    type: f.type,
    inventory_field_id: f.inventory_field_id,
    field_layout_id: f.field_layout_id,
    inventory_type: f.inventory_type,
    nested_binding: f.nested_binding ?? undefined,
    palette_source: f.palette_source,
    name: f.name,
    list_group_id: f.list_group_id,
    row: f.row,
    cell: f.cell,
  })
  emit('dragstart', payload, event)
}

function maxSpan(): number {
  return ROW_COLS - props.field.cell
}

let startX = 0
let startLen = 0
let moveHandler: ((e: PointerEvent) => void) | null = null
let upHandler: (() => void) | null = null

function teardownResizeListeners() {
  if (moveHandler) document.removeEventListener('pointermove', moveHandler)
  if (upHandler) document.removeEventListener('pointerup', upHandler)
  moveHandler = null
  upHandler = null
}

function onResizePointerDown(event: PointerEvent) {
  if (props.field.view_type === 'table') return

  event.preventDefault()
  event.stopPropagation()

  isResizing.value = true
  startX = event.clientX
  startLen = props.field.length
  previewSpan.value = startLen

  const rowEl = rootRef.value?.closest('.pb-grid-row') as HTMLElement | null
  const containerWidth = rowEl?.getBoundingClientRect().width ?? 0
  const colW = containerWidth > 0 ? containerWidth / ROW_COLS : 1

  moveHandler = (e: PointerEvent) => {
    if (!colW) return
    const deltaCols = Math.round((e.clientX - startX) / colW)
    const next = Math.max(MIN_SPAN, Math.min(maxSpan(), startLen + deltaCols))
    previewSpan.value = next
  }

  upHandler = () => {
    teardownResizeListeners()
    const finalSpan = previewSpan.value ?? startLen
    previewSpan.value = null
    isResizing.value = false
    const clamped = Math.max(MIN_SPAN, Math.min(maxSpan(), finalSpan))
    if (clamped !== props.field.length) {
      emit('resize', {
        id: props.field.id,
        row: props.field.row,
        cell: props.field.cell,
        newSpan: clamped,
      })
    }
  }

  document.addEventListener('pointermove', moveHandler)
  document.addEventListener('pointerup', upHandler)
}

onBeforeUnmount(() => {
  teardownResizeListeners()
})
</script>

<template>
  <div
    ref="rootRef"
    class="pb-field-card"
    :class="cardClass"
    :style="gridColumnStyle"
    :data-pb-field-id="String(field.id)"
    :data-pb-field-row="String(field.row)"
    :data-pb-field-cell="String(field.cell)"
    :draggable="!isResizing && !editing"
    @dragstart="onDragStart"
  >
    <input
      v-if="editing"
      ref="renameInputRef"
      v-model="renameDraft"
      class="pb-fc-name-input"
      type="text"
      aria-label="Переименовать поле"
      @blur="commitRename"
      @keydown.enter.prevent="commitRename"
      @keydown.escape.prevent="cancelRename"
      @click.stop
      @pointerdown.stop
    />
    <div
      v-else
      class="pb-fc-name"
      :title="displayName"
      @dblclick.prevent="startRename"
    >
      {{ displayName }}
    </div>
    <div class="pb-fc-meta">
      <span v-if="fieldTypeBadge" class="pb-fc-type">{{ fieldTypeBadge }}</span>
      <span v-if="paletteSource === 'parent' && parentBadge" class="pb-fc-badge pb-fc-badge--teal">{{ parentBadge }}</span>
      <VIcons v-if="paletteSource !== 'own'" class="pb-fc-lock" type="lock" />
    </div>

    <div
      v-if="field.view_type !== 'table'"
      class="pb-fc-resize"
      @pointerdown="onResizePointerDown"
    />

    <VBtn
      class="pb-field-card-delete"
      icon
      variant="text"
      size="x-small"
      density="compact"
      aria-label="Удалить поле"
      @click.stop="emit('remove', { id: field.id, row: field.row, cell: field.cell })"
    >
      <VIcons type="close" />
    </VBtn>

    <VBtn
      v-if="paletteSource === 'parent' || paletteSource === 'element'"
      class="pb-field-card-settings"
      icon
      variant="text"
      size="x-small"
      density="compact"
      :aria-label="$t('itf.settings')"
      @click.stop="openSettings"
    >
      <VIcons type="settings" />
    </VBtn>

    <VTooltip
      v-else-if="fieldInfo"
      location="top"
      :open-delay="300"
    >
      <template #activator="{ props: tooltipProps }">
        <div
          v-bind="tooltipProps"
          class="pb-field-card-info"
        >
          <VIcons type="info" />
        </div>
      </template>
      <span>{{ fieldInfo }}</span>
    </VTooltip>
  </div>
</template>
